/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Loader from '../../components/loader';
import Dashboard, { DashboardColumn, DashboardRow, DashboardSection, DashboardSectionTitle } from '../../components/dashboard';
import { DATE, formatDate } from '../../utils/date';
import { useParams } from 'react-router';
import { AdminBreadcrumbs } from './AdminBreadcrumbs';
import { Button } from 'reactstrap';
import Table, { TableColumn } from '../../components/table/Table';
import { SurveySummary } from '../../types/survey';
import { getPeriodName } from '../../utils/universalTracker';
import { Link } from 'react-router-dom';
import { getFullName } from '../../utils/user';
import { useGetStatsQuery, useGetUserSurveyDetailsQuery, useGetUserSurveyStatsQuery } from '../../api/initiative-stats';
import { BasicAlert } from '../../components/alert/BasicAlert';
import { UserMessageModal } from '../../components/message-modal/MessageModal';
import { generateUrl } from '../util';
import { ROUTES } from '../../constants/routes';
import G17Client from '../../services/G17Client';
import SurveyQuestionList from '../../components/survey-question-list/survey-question-list';
import { ReportSwitcherContainer } from '../../components/initiative/ReportSwitcherContainer';
import { ColumnTitle } from '../../components/survey-question-list/partials/ColumnTitle';
import { Divider } from '../../components/tracking-list';
import { ColumnValue } from '../../components/survey-question-list/partials/ColumnValue';
import { ColumnStatusIcon } from '../../components/survey-question-list/partials/ColumnStatusIcon';
import { BulkActionToolbar } from '../../components/survey-question-list/partials/BulkActionToolbar';
import LoadingPlaceholder from '../../components/LoaderContainer/LoadingPlaceholder';
import { getSurveyName } from '../../utils/surveyData';
import { SurveyQuestionListToolbar } from '../../components/survey/survey-question-list-toolbar';
import { ScopeViewDropdown } from '../../components/survey/survey-question-list-toolbar/partials/ScopeViewDropdown';
import { Spacer } from '../../components/survey/survey-question-list-toolbar/partials/Spacer';
import { useSurveyGroups } from '../../utils/survey';
import { useAppSelector } from '../../reducers';
import { ExpandAllToggle } from '../../components/survey/survey-question-list-toolbar/partials/ExpandAllToggle';
import { SelectAllToggle } from '../../components/survey/survey-question-list-toolbar/partials/SelectAllToggle';
import { naturalSort } from '../../utils';
import { InitiativePermissions } from '../../services/permissions/InitiativePermissions';
import { Menu } from '../../components/menu/Menu';
import { QUESTION } from '../../constants/terminology';
import { ColumnBookmark } from '../../components/survey-question-list/partials/ColumnBookmark';

interface RouteParams {
  initiativeId: string,
  userId: string;
}

interface SurveyStats extends Pick<SurveySummary,
  '_id' | 'status' | 'name' | 'period' | 'effectiveDate' | 'initiativeId' | 'type'
> {
  lastUpdated: string;
  initiativeName?: string;
}

export const AdminUserRoute = () => {

  const { initiativeId, userId } = useParams<RouteParams>();

  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [filter, setFilter] = useState<'pending' | 'completed'>('pending')
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedSurveyId, setSelectedSurveyId] = useState<string | undefined>(undefined);
  const [isCompleted, setIsCompleted] = useState(false);

  const isCompleteFilter = filter === 'completed';

  const surveyDetails = useGetUserSurveyDetailsQuery({
    initiativeId,
    surveyId: selectedSurveyId ?? '',
    userId
  }, {
    skip: !selectedSurveyId
  });
  const surveyStats = useGetUserSurveyStatsQuery({
    initiativeId,
    userId,
    isCompleted: isCompleteFilter,
  })

  const user = surveyStats.data?.user;
  const canContributeInitiative = user ? InitiativePermissions.canContribute(user, initiativeId) : false;
  const canVerifyInitiative = user ? InitiativePermissions.canVerify(user, initiativeId) : false;

  const fetchInitiativeSurveys = useMemo(() => {
    if (canContributeInitiative || canVerifyInitiative) {
      return true;
    }
    return false;
  }, [canContributeInitiative, canVerifyInitiative]);

  const totalStats = useGetStatsQuery({ initiativeId }, { skip: !fetchInitiativeSurveys });

  useEffect(() => {
    if (!surveyStats.isSuccess) {
      return;
    }
    const firstSurvey = surveyStats.data.surveys[0];
    if (!firstSurvey) {
      return;
    }
    setSelectedSurveyId(firstSurvey._id);
    setIsCompleted(Boolean(firstSurvey.completedDate));
  }, [surveyStats, filter])

  const overviewMode = useAppSelector(state => state.surveySettings.overviewMode);
  const surveyGroups = useSurveyGroups(overviewMode, surveyDetails.data);

  const breadcrumbs = [
    { label: 'Admin Dashboard', url: generateUrl(ROUTES.ADMIN_DASHBOARD, { initiativeId }) },
    { label: 'User View' },
  ];

  const updateStates = useCallback((statsIsLoading: boolean, statsIsError: boolean) => {
    if (isError !== statsIsError) {
      setIsError(statsIsError);
    }
    if (isLoading !== statsIsLoading) {
      setIsLoading(statsIsLoading);
    }
  }, [isError, isLoading]);

  const surveyData: SurveyStats[] = useMemo(() => {
    if (fetchInitiativeSurveys) {
      updateStates(totalStats.isLoading, totalStats.isError);
      if (!totalStats.isSuccess) {
        return [];
      }
      return totalStats.data
        .reduce((acc, { surveys }) => {
          const filteredSurveys = surveys.filter(s => isCompleteFilter ? s.completedDate : !s.completedDate);
          if (filteredSurveys.length > 0) {
            return [...acc, ...filteredSurveys];
          }
          return acc;
        }, [] as SurveyStats[]);
    }
    updateStates(surveyStats.isLoading, surveyStats.isError);
    if (!surveyStats.isSuccess) {
      return [];
    }
    return surveyStats.data.surveys
  }, [fetchInitiativeSurveys, updateStates, totalStats, surveyStats, isCompleteFilter]);

  if (isLoading || !user) {
    return <Loader />;
  }

  if (isError) {
    return (
      <div className='admin-dashboard-overview'>
        <Dashboard>
          <DashboardRow>
            <AdminBreadcrumbs
              breadcrumbs={breadcrumbs}
              initiativeId={initiativeId}
            />
          </DashboardRow>
        </Dashboard>

        <Dashboard className='admin-dashboard'>
          <DashboardRow title={'Admin dashboard'}>
            <DashboardColumn flexBasisPc={'100%'}>
              <BasicAlert type={'danger'}>{surveyStats.error?.message ?? 'There was a problem loading user information'}</BasicAlert>
            </DashboardColumn>
          </DashboardRow>
        </Dashboard>
      </div>
    );
  }

  const handleReload = () => surveyDetails.refetch();
  const handleChangeSurvey = (newSurveyId: string) => {
    if (!surveyStats.isSuccess) {
      return;
    }
    const selectedSurvey = surveyStats.data.surveys.find(survey => survey._id === newSurveyId);
    if (!selectedSurvey) {
      return;
    }
    setSelectedSurveyId(selectedSurvey._id);
    setIsCompleted(Boolean(selectedSurvey.completedDate));
  }

  const downloadXlsx = async () => {
    return G17Client.downloadUserStatsView(initiativeId, userId).catch(e => console.log(e));
  }
  const columns: TableColumn<SurveyStats>[] = [
    {
      Header: 'Type',
      accessor: (row) => getPeriodName(row.period, false),
    },
    {
      id: 'date',
      Header: 'Up to',
      accessor: (row) => formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT),
      sortType: (a, b) => naturalSort(a.original.effectiveDate, b.original.effectiveDate),
    },
    {
      cellProps: {
        style: {
          maxWidth: 250
        }
      },
      Header: 'Title',
      accessor: (row) => {
        const url = generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId: row.initiativeId, surveyId: row._id, page: 'overview' });
        return <Link to={`${url}?filterByDelegationStatus[]=${user._id}`}>
          <span className={'text-truncate'} style={{ display: 'block' }}>
            {row.name ?? row.initiativeName ?? '-'}
          </span>
        </Link>;
      },
    },
    {
      Header: 'Updated',
      accessor: (row: SurveyStats) => formatDate(row.lastUpdated, DATE.HUMANIZE),
    },
    {
      Header: () => <i className='fa fa-user-clock text-secondary mr-2' />,
      accessor: 'status.created',
      cellProps: { style: { width: 20, } }
    },
    {
      Header: () => <i className='fa fa-user-edit text-primary mr-2' />,
      accessor: 'status.updated',
      cellProps: { style: { width: 20, } }
    },
    {
      Header: () => <i className='fa fa-user-times text-danger mr-2' />,
      accessor: 'status.rejected',
      cellProps: { style: { width: 20, } }
    },
    {
      Header: () => <i className='fa fa-user-check text-success mr-2' />,
      accessor: 'status.verified',
      cellProps: { style: { width: 20, } }
    },
  ];

  return (
    <div className='admin-dashboard-overview'>
      <Dashboard className='admin-dashboard'>
        <DashboardRow>
          <AdminBreadcrumbs breadcrumbs={breadcrumbs} initiativeId={initiativeId} />
        </DashboardRow>

        <DashboardRow
          className={'header-row'}
          title={<>Admin dashboard: {getFullName(user)}<Button color='link' className='ml-2'><i className='fa-solid fa-file-excel' onClick={() => downloadXlsx()} /></Button></>}
          buttons={[
            <Button color={'secondary'} outline onClick={() => setShowMessageModal(true)}>
              Message
            </Button>,
          ]} >
          <div className='ml-2 mb-3 header-subtitle'>
            <span className={'pr-1 mr-1 header-subtitle-prefix'}>User</span>
            <span>Last online {formatDate(user.lastLogin ?? '', DATE.HUMANIZE) ?? '-'}</span>
          </div>
        </DashboardRow>

        <DashboardSectionTitle title='Surveys' />

        <DashboardSection className={'mb-5'}>
          <Menu grouped items={[
            {
              label: 'In Progress',
              onClick: () => setFilter('pending'),
              active: !isCompleteFilter,
            },
            {
              label: 'Completed',
              onClick: () => setFilter('completed'),
              active: isCompleteFilter
            }
          ]} />

          {(surveyStats.isLoading || surveyStats.isFetching) ? <Loader relative /> : <Table
            defaultSortIcon={null}
            showRowCount={false}
            responsive={false}
            columns={columns}
            data={surveyData}
            sortBy={[{ id: 'date', desc: false }]}
            noData={<BasicAlert type='info'>The selected user has no surveys assigned</BasicAlert>}
          />
          }
        </DashboardSection>

        {surveyData.length > 0 ?
          <>
            <DashboardSectionTitle title={`Survey ${QUESTION.PLURAL}`} buttons={[
              <ReportSwitcherContainer
                surveyList={surveyData.map(r => ({ ...r, name: getSurveyName({ ...r, initiatives: [{ name: r.initiativeName }] }) }))}
                initiativeId={initiativeId}
                selectedSurveyId={selectedSurveyId}
                onChange={handleChangeSurvey}
                defaultToCurrent={false}
              />
            ]} />

            <DashboardSection className={'mb-5'} >
              <LoadingPlaceholder isLoading={surveyDetails.isFetching} height={300}>
                {selectedSurveyId && surveyDetails.isSuccess ? (
                  <SurveyQuestionList
                    surveyId={selectedSurveyId}
                    initiativeId={initiativeId}
                    surveyGroups={surveyGroups}
                    bulkActionToolbar={isCompleted ? undefined : (props) => surveyDetails.currentData ? <BulkActionToolbar {...props} surveyData={surveyDetails.currentData} handleReload={handleReload} /> : undefined}
                    columns={[
                      ColumnBookmark,
                      ColumnTitle,
                      Divider,
                      ColumnValue,
                      Divider,
                      ColumnStatusIcon
                    ]}
                    toolbar={(props) => (
                      selectedSurveyId && surveyDetails.isSuccess ? (
                        <SurveyQuestionListToolbar
                          {...props}
                          surveyScope={surveyDetails.data.scope}
                          components={[
                            ScopeViewDropdown,
                            Spacer,
                            ExpandAllToggle,
                            !isCompleted ? SelectAllToggle : null,
                          ]}
                        />
                      ) : null
                    )}
                  />)
                  :
                  <BasicAlert type='info'>Please select a survey</BasicAlert>
                }
              </LoadingPlaceholder>
            </DashboardSection>
          </>
          : null}
      </Dashboard>
      {showMessageModal ? <UserMessageModal users={[user]} userIds={[user._id]} toggle={() => setShowMessageModal(!showMessageModal)} /> : null}
    </div>
  );
}
